import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type Resolver, type UseFormReturn } from "react-hook-form";

import {
  CampaignType,
  type IEndCampaignFormValues,
  type IEndCampaignTarget,
} from "@/@types/campaign";
import { useToast } from "@/components/ui/use-toast";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useUpdatePro013CampaignMutation,
  useUpdatePro017CampaignMutation,
} from "@/services/campaign";
import { useUpdatePro014CampaignMutation } from "@/services/campaign-pro014";
import { createEndCampaignSchema } from "@/services/validation-schemas/end-campaign";
import type { IUseEndCampaignResult } from "../types/end-campaign.types";
import type { CampaignConflictItem } from "../utils/conflict-resolution";
import { conflictsOf, endDateLimitsOf } from "../utils/end-campaign";
import { getTranslatedErrorMessage } from "../utils/error-messages";
import { MESSAGES } from "../utils/messages";

type EndCampaignTrigger = (args: { code: number; endDate: string }) => {
  unwrap: () => Promise<unknown>;
};

export function useEndCampaign({
  campaignCode,
  campaignType,
  startDate,
  currentEndDate,
}: IEndCampaignTarget): IUseEndCampaignResult {
  const { toast } = useToast();
  const { handleModal } = useDialogModal();
  const [updatePro013, pro013State] = useUpdatePro013CampaignMutation();
  const [updatePro014, pro014State] = useUpdatePro014CampaignMutation();
  const [updatePro017, pro017State] = useUpdatePro017CampaignMutation();
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);

  const resolver: Resolver<IEndCampaignFormValues> = useMemo(
    (): Resolver<IEndCampaignFormValues> =>
      zodResolver(
        createEndCampaignSchema(endDateLimitsOf(startDate, currentEndDate)),
      ),
    [startDate, currentEndDate],
  );
  const form: UseFormReturn<IEndCampaignFormValues> = useForm<IEndCampaignFormValues>({
    resolver,
    defaultValues: { endDate: "" },
  });

  const triggers: Partial<Record<CampaignType, EndCampaignTrigger>> = {
    [CampaignType.PRO013]: updatePro013,
    [CampaignType.PRO014]: updatePro014,
    [CampaignType.PRO017]: updatePro017,
  };
  const isLoading: boolean =
    pro013State.isLoading || pro014State.isLoading || pro017State.isLoading;

  const close = (): void =>
    handleModal({ isOpen: false, element: null, title: "", description: "" });

  const clearConflicts = (): void => setConflicts([]);

  const submit = async (values: IEndCampaignFormValues): Promise<void> => {
    const endCampaign: EndCampaignTrigger | undefined = triggers[campaignType];
    if (!endCampaign) return;

    try {
      await endCampaign({ code: campaignCode, endDate: values.endDate }).unwrap();
    } catch (failure: unknown) {
      const found: CampaignConflictItem[] = conflictsOf(failure);
      setConflicts(found);
      if (found.length > 0) return;

      toast({
        ...MESSAGES.END.ERROR,
        description: getTranslatedErrorMessage(
          failure,
          MESSAGES.END.ERROR.description,
        ),
      });
      return;
    }

    toast(MESSAGES.END.SUCCESS);
    close();
  };

  return { form, isLoading, conflicts, submit, close, clearConflicts };
}
